import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Box, CircularProgress, FormControl, InputLabel, Select, MenuItem, Grid, SelectChangeEvent, Paper, Button } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { Restaurant, Cuisine, Dish } from '../types';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

// Create custom marker icon for ratings
const createRatingIcon = (rating: number | null | undefined) => {
  // Determine color based on rating
  let color = '#FF0000'; // Red for low ratings
  if (rating === null || rating === undefined) {
    color = '#9E9E9E'; // Gray for no ratings
  } else if (rating >= 4) {
    color = '#4CAF50'; // Green for high ratings
  } else if (rating >= 3) {
    color = '#FFC107'; // Yellow for medium ratings
  } else if (rating >= 2) {
    color = '#FF9800'; // Orange for low-medium ratings
  }

  // Create a div element for the marker
  const divIcon = L.divIcon({
    className: 'custom-rating-marker',
    html: `
      <div style="
        background-color: ${color};
        color: white;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">
        ${rating !== null && rating !== undefined ? rating.toFixed(1) : 'N/A'}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  return divIcon;
};

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const DEFAULT_LOCATION: [number, number] = [41.0082, 28.9784];

const NearbyPage: React.FC = () => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [cuisines, setCuisines] = useState<Cuisine[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [selectedCuisine, setSelectedCuisine] = useState<string>('all');
  const [selectedDish, setSelectedDish] = useState<string>('');
  const [showDishFilter, setShowDishFilter] = useState<boolean>(false);
  const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserLocation = () => {
      console.log('Fetching user location...');
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location: [number, number] = [position.coords.latitude, position.coords.longitude];
            console.log('Setting user location:', location);
            setUserLocation(location);
          },
          (error) => {
            console.error('Error getting location:', error);
            console.log('Setting default location:', DEFAULT_LOCATION);
            setUserLocation(DEFAULT_LOCATION);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      } else {
        console.log('Geolocation not supported, setting default location:', DEFAULT_LOCATION);
        setUserLocation(DEFAULT_LOCATION);
      }
    };

    const fetchData = async () => {
      try {
        const [restaurantsRes, cuisinesRes, dishesRes] = await Promise.all([
          axios.get('/api/restaurants/'),
          axios.get('/api/cuisines/'),
          axios.get('/api/dishes/')
        ]);
        
        setRestaurants(restaurantsRes.data.results || []);
        setCuisines(cuisinesRes.data.results || []);
        setDishes(dishesRes.data.results || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setRestaurants([]);
        setCuisines([]);
        setDishes([]);
        setLoading(false);
      }
    };

    fetchUserLocation();
    fetchData();
  }, []);

  const fetchRestaurants = async (bounds: L.LatLngBounds | null) => {
    if (!bounds) return;

    try {
      const params: any = {
        min_lat: bounds.getSouth(),
        max_lat: bounds.getNorth(),
        min_lng: bounds.getWest(),
        max_lng: bounds.getEast(),
        limit: 1000
      };

      // Only add cuisine_id if it's not 'all'
      if (selectedCuisine !== 'all') {
        params.cuisine_id = selectedCuisine;
      }

      // Only add dish_id if it's not empty
      if (selectedDish) {
        params.dish_id = selectedDish;
      }

      console.log('Fetching restaurants with params:', params);
      const response = await api.get('/api/restaurants/', { params });
      console.log('Restaurants response:', response.data);

      if (Array.isArray(response.data)) {
        setRestaurants(response.data);
      } else if (response.data.results) {
        setRestaurants(response.data.results);
      } else {
        console.error('Unexpected response format:', response.data);
        setRestaurants([]);
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError('Failed to fetch restaurants');
    }
  };

  // Map bounds change handler
  const MapBoundsHandler = () => {
    const map = useMapEvents({
      moveend: () => {
        setMapBounds(map.getBounds());
      },
      zoomend: () => {
        setMapBounds(map.getBounds());
      },
    });
    return null;
  };

  // Effect to fetch restaurants when filters or bounds change
  useEffect(() => {
    if (mapBounds) {
      fetchRestaurants(mapBounds);
    }
  }, [mapBounds, selectedCuisine, selectedDish]);

  const handleCuisineChange = async (event: SelectChangeEvent) => {
    const cuisineId = event.target.value;
    setSelectedCuisine(cuisineId);
    setSelectedDish('');

    if (cuisineId === 'all') {
      setShowDishFilter(false);
      setDishes([]);
      return;
    }

    setShowDishFilter(true);
    try {
      console.log('Fetching dishes for cuisine:', cuisineId);
      const response = await api.get('/api/dishes/', {
        params: { cuisine_id: cuisineId }
      });
      console.log('Dishes response:', response.data);

      if (Array.isArray(response.data)) {
        setDishes(response.data);
      } else if (response.data.results) {
        setDishes(response.data.results);
      } else {
        console.error('Unexpected response format:', response.data);
        setDishes([]);
      }
    } catch (error) {
      console.error('Error fetching dishes:', error);
      setError('Failed to fetch dishes');
      setDishes([]);
    }
  };

  const handleDishChange = (event: SelectChangeEvent) => {
    setSelectedDish(event.target.value);
  };

  const handleRestaurantSelect = (restaurant: Restaurant) => {
    navigate(`/restaurant/${restaurant.id}`);
  };

  // Map bounds change handler
  const handleMapBoundsChange = useCallback((bounds: L.LatLngBounds) => {
    console.log('Map bounds changed:', bounds);
    setMapBounds(bounds);
  }, []);

  if (loading || !userLocation) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  console.log('Rendering map with userLocation:', userLocation);

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      {/* Map Controls Overlay */}
      <Paper 
        sx={{ 
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          p: 2,
          width: 300,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Cuisine</InputLabel>
              <Select
                value={selectedCuisine}
                label="Cuisine"
                onChange={handleCuisineChange}
              >
                <MenuItem value="all">All Cuisines</MenuItem>
                {cuisines.map((cuisine) => (
                  <MenuItem key={cuisine.id} value={cuisine.id.toString()}>
                    {cuisine.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {showDishFilter && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Dish</InputLabel>
                <Select
                  value={selectedDish}
                  label="Dish"
                  onChange={handleDishChange}
                >
                  <MenuItem value="">All Dishes</MenuItem>
                  {dishes.map((dish) => (
                    <MenuItem key={dish.id} value={dish.id.toString()}>
                      {dish.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Map View */}
      <div style={{ 
        height: '100%', 
        width: '100%',
        position: 'relative',
        zIndex: 0
      }}>
        <MapContainer
          center={userLocation}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          whenReady={() => console.log('Map is ready')}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapBoundsHandler />
          {restaurants.map((restaurant) => {
            console.log('Rendering restaurant:', restaurant);
            const position: [number, number] = [restaurant.latitude, restaurant.longitude];
            return (
              <Marker
                key={restaurant.id}
                position={position}
                icon={createRatingIcon(restaurant.average_rating || null)}
                eventHandlers={{
                  click: () => handleRestaurantSelect(restaurant),
                }}
              >
                <Popup>
                  <div>
                    <h3>{restaurant.name}</h3>
                    <p>{restaurant.cuisines?.map(c => c.name).join(', ') || 'No cuisines listed'}</p>
                    <p>Rating: {restaurant.average_rating || 'No ratings yet'}</p>
                    <p>Price: {restaurant.price_range ? '$'.repeat(restaurant.price_range) : 'N/A'}</p>
                    {restaurant.address && <p>{restaurant.address}</p>}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

export default NearbyPage; 